import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../../../lib/axios';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Plus, Search, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API, getAuthHeader, formatCurrency } from '../crmUtils';
import { CurrencyInput } from '../../ui/currency-input';
import CadastroRapidoProdutoDialog from '../../CadastroRapidoProdutoDialog';

export const SubItensCatalogo = ({ item, idx, updateItem }) => {
  const [busca, setBusca] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showCadastroRapido, setShowCadastroRapido] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/produtos');
        const prods = res.data?.items || res.data || [];
        const cats = new Set(); const mrs = new Set();
        prods.forEach(p => { if (p.categoria) cats.add(p.categoria); if (p.marca) mrs.add(p.marca); });
        setCategorias([...cats].sort());
        setMarcas([...mrs].sort());
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!busca || busca.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await api.get(`/propostas/buscar/produtos?q=${encodeURIComponent(busca)}`);
        setResults(res.data || []);
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [busca]);

  const addFromCatalog = (produto) => {
    const newSub = {
      id: crypto.randomUUID(),
      produto_id: produto.id,
      produto_nome: produto.nome,
      produto_sku: produto.sku || '',
      quantidade: 1,
      preco_unitario: produto.preco_venda || 0,
    };
    const subs = [...(item.sub_itens || []), newSub];
    updateItem(idx, { ...item, sub_itens: subs });
    setBusca('');
    setResults([]);
  };

  const updateSub = (siIdx, field, value) => {
    const newSubs = [...(item.sub_itens || [])];
    newSubs[siIdx] = { ...newSubs[siIdx], [field]: value };
    updateItem(idx, { ...item, sub_itens: newSubs });
  };

  const removeSub = (siIdx) => {
    const newSubs = (item.sub_itens || []).filter((_, i) => i !== siIdx);
    updateItem(idx, { ...item, sub_itens: newSubs });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <Label className="text-[10px] text-slate-600 font-medium">Sub-itens do catálogo</Label>
        <Button type="button" variant="outline" size="sm" className="h-6 text-[10px] px-2 gap-1 text-emerald-700 border-emerald-200"
          onClick={() => setShowCadastroRapido(true)}>
          <Plus className="w-3 h-3" /> Cadastro Rápido
        </Button>
      </div>
      <div className="relative mb-2">
        <Search className="absolute left-2 top-1.5 w-3.5 h-3.5 text-slate-400" />
        <Input value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="Buscar produto do catálogo..." className="h-7 text-xs pl-7" />
        {searching && <Loader2 className="absolute right-2 top-1.5 w-3.5 h-3.5 animate-spin text-slate-400" />}
        {results.length > 0 && (
          <div className="absolute z-20 mt-0.5 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
            {results.map(p => (
              <button key={p.id} type="button" className="w-full text-left px-2.5 py-1.5 text-xs hover:bg-slate-50 flex justify-between items-center"
                onClick={() => addFromCatalog(p)}>
                <div className="flex items-center gap-1.5">
                  <Package className="w-3 h-3 text-emerald-500" />
                  <span className="font-medium">{p.nome}</span>
                  {p.sku && <span className="text-slate-400">({p.sku})</span>}
                </div>
                <span className="font-semibold text-emerald-600">{formatCurrency(p.preco_venda)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {(item.sub_itens || []).length === 0 ? (
        <p className="text-[10px] text-slate-400 italic py-1">Nenhum sub-item. Busque produtos acima para adicionar.</p>
      ) : (
        <div className="space-y-1">
          {(item.sub_itens || []).map((si, siIdx) => (
            <div key={si.id} className="flex items-center gap-2 bg-slate-50 rounded px-2 py-1">
              <Package className="w-3 h-3 text-emerald-500 flex-shrink-0" />
              <span className="text-xs font-medium text-slate-700 flex-1 truncate">{si.produto_nome}</span>
              {si.produto_sku && <span className="text-[10px] text-slate-400">{si.produto_sku}</span>}
              <Input type="number" min="1" value={si.quantidade || 1}
                onChange={e => updateSub(siIdx, 'quantidade', parseInt(e.target.value) || 1)}
                className="h-6 text-xs w-14 text-center" />
              {item.preco_por_subitem && (
                <CurrencyInput value={si.preco_unitario || 0}
                  onChange={v => updateSub(siIdx, 'preco_unitario', v)}
                  className="h-6 text-xs w-28" placeholder="Valor" />
              )}
              {item.preco_por_subitem && <span className="text-[10px] font-semibold text-slate-600 w-20 text-right">{formatCurrency((si.preco_unitario || 0) * (si.quantidade || 1))}</span>}
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400" onClick={() => removeSub(siIdx)}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <CadastroRapidoProdutoDialog
        open={showCadastroRapido}
        onClose={() => setShowCadastroRapido(false)}
        origem="Proposta (sub-item)"
        testIdPrefix="quick-sub"
        categoriasLookup={categorias}
        marcasLookup={marcas}
        onSuccess={(novoProduto) => {
          addFromCatalog({ id: novoProduto.id, nome: novoProduto.nome, sku: novoProduto.sku, preco_venda: novoProduto.preco_venda || 0 });
        }}
      />
    </div>
  );
};
